<template>
  <tr class="even:bg-white odd:bg-gray-50">
    <td class="px-4 py-4 text-sm text-gray-500">
      <div class="flex items-center space-x-3">
        <div class="flex-none">
          <div
            class="p-2 rounded-full"
            :class="[
              {
                'bg-green-100': gaCampaign.status_name === 'ENABLED',
                'bg-gray-100': gaCampaign.status_name === 'PAUSED',
                'bg-red-100': gaCampaign.status_name === 'REMOVED',
                'bg-teal-100': ['UNKNOWN', 'UNSPECIFIED'].indexOf(gaCampaign.status_name) >= 0,
              },
            ]"
            v-tip="{ content: gaCampaign.status_name }"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 block"
              :class="[
                {
                  'text-green-600': gaCampaign.status_name === 'ENABLED',
                  'text-gray-600': gaCampaign.status_name === 'PAUSED',
                  'text-red-600': gaCampaign.status_name === 'REMOVED',
                  'text-teal-600': ['UNKNOWN', 'UNSPECIFIED'].indexOf(gaCampaign.status_name) >= 0,
                },
              ]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <template v-if="gaCampaign.status_name === 'ENABLED'">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </template>
              <path
                v-else-if="gaCampaign.status_name === 'REMOVED'"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
              <path
                v-else-if="gaCampaign.status_name === 'PAUSED'"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
              <path
                v-else
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>
        <div class="flex-1">
          <p>{{ gaCampaign.name }}</p>
          <span class="text-xs text-gray-600">{{ gaCampaign.id }}</span>
          -
          <span>{{ customerName }}</span>
        </div>
      </div>
    </td>
    <td class="px-4 py-4 text-center text-sm text-gray-500">
      <span
        class="p-2 rounded-full inline-block"
        :class="[linkedToAdPlans > 0 ? 'bg-green-100' : 'bg-gray-100']"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-4 w-4 block"
          :class="[linkedToAdPlans > 0 ? 'text-green-600' : 'text-gray-600']"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          v-tip="{
            content() {
              if (gaCampaign.campaigns_count) {
                return `Linked to ${gaCampaign.campaigns_count} Ad Plans`;
              }

              return 'Not Linked';
            },
          }"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
          />
        </svg>
      </span>
    </td>
    <td class="px-4 py-4 text-right text-sm text-gray-900">
      {{ metrics.impressions }}
    </td>
    <td class="px-4 py-4 text-right text-sm text-gray-900">
      {{ metrics.clicks }}
    </td>
    <td class="px-4 py-4 text-right text-sm text-gray-900">
      {{ metrics.interactions }}
    </td>
    <td class="px-4 py-4 text-right text-sm text-gray-900">
      {{ metrics.engagements }}
    </td>
    <td class="px-4 py-4 text-sm text-gray-900 text-right">
      {{ metrics.cost }}
    </td>
  </tr>
</template>
<script>
import Numbro from '@/utils/numbro';
import { isNumber } from 'lodash-es';

export default {
  props: {
    gaCampaign: {
      type: Object,
      required: true,
    },
  },
  computed: {
    customerName() {
      return this.gaCampaign?.ga_customer?.name || 'N/A';
    },
    linkedToAdPlans() {
      return this.gaCampaign.campaigns_count > 0;
    },
    metrics() {
      const defaultMetrics = {
        impressions: 'N/A',
        clicks: 'N/A',
        interactions: 'N/A',
        engagements: 'N/A',
        cost: 'N/A',
      };

      const { lifetime_report: metrics } = this.gaCampaign;

      if (!metrics) {
        return defaultMetrics;
      }

      if (isNumber(metrics.impressions)) {
        defaultMetrics.impressions = Numbro(metrics.impressions).format({
          thousandSeparated: true,
        });
      }

      if (isNumber(metrics.clicks)) {
        defaultMetrics.clicks = Numbro(metrics.clicks).format({
          thousandSeparated: true,
        });
      }

      if (isNumber(metrics.interactions)) {
        defaultMetrics.interactions = Numbro(metrics.interactions).format({
          thousandSeparated: true,
        });
      }

      if (isNumber(metrics.engagements)) {
        defaultMetrics.engagements = Numbro(metrics.engagements).format({
          thousandSeparated: true,
        });
      }

      if (isNumber(metrics.cost_micros)) {
        defaultMetrics.cost = Numbro(metrics.cost_micros / 1_000_000).formatCurrency({
          thousandSeparated: true,
          mantissa: 2,
        });
      }

      return defaultMetrics;
    },
  },
};
</script>
